import React, { ReactNode, useCallback } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { varColor } from "../../utils/themeHelpers";
import { typographyCoreStyles, typographyStyles } from "../common/Typography";
import { ButtonReset } from "./Button.mixins";

const TabButton = styled.button<{ isSelected: boolean }>(
    ButtonReset,
    typographyCoreStyles,
    typographyStyles.h4,
    {
        height: `${42 / 16}rem`,
        lineHeight: 22 / 13,
        paddingInline: `${10 / 16}rem`,
        paddingBlock: `${10 / 16}rem`,
        borderTopLeftRadius: `${4 / 16}rem`,
        borderTopRightRadius: `${4 / 16}rem`,
    },
    props => ({
        transition: "color 333ms, background-color 333ms",
        color: props.isSelected
            ? varColor("primary", "white")
            : varColor("primary", "silver"),
    }),
    css`
        &:focus:not(:hover) {
            background-color: ${varColor("primary", "steel02")};
        }

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }
    `,
);

const TabItem = styled.li<{ isSelected: boolean }>`
    position: relative;
    flex-shrink: 0;

    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background-color: var(--formant-color-highlights-blue);
        transition:
            transform 333ms ease-out,
            opacity 333ms;
        transform: scaleY(${props => (props.isSelected ? 1 : 0)});
        opacity: ${props => (props.isSelected ? 1 : 0)};
        transform-origin: bottom center;
    }
`;

interface IPickerTabProps<T extends string = string> {
    id: T;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect?: (tab: T) => void;
    children?: ReactNode;
    "data-testid"?: string;
}

export function PickerTabButton<T extends string = string>(
    props: IPickerTabProps<T>,
) {
    const {
        id,
        isSelected,
        onSelect,
        isDisabled,
        children,
        "data-testid": dataTestId,
    } = props;
    const handleOnClick = useCallback(() => {
        onSelect?.(id);
    }, [onSelect, id]);

    return (
        <TabItem isSelected={isSelected}>
            <TabButton
                onClick={handleOnClick}
                isSelected={isSelected}
                disabled={isDisabled}
                data-testid={dataTestId}
            >
                {children}
            </TabButton>
        </TabItem>
    );
}
